// What the admin has entered in the build flow's first four steps, as it is
// kept with the program so Continue on its card reopens where they left. It
// rides in the program's saved build state (under requirements.trainingFlow),
// which the Structure, Content and Members steps carry forward as they save.
// No React here, and of the wizard only its types, so it can be tested on its own.

import {
  applyAudienceMatchTypes,
  audienceRulesFromFlat,
  type AudienceRule,
  type MemberAttributeGroupCatalog
} from '../../../utils/memberAttributes';
import type { EnrollmentMode, ProgramTypeKey } from '../../Program/components/learning-lifecycle/types';
import type { AIMDv2BuildState, RequirementsData } from '../../aimd-v2/types';
import type { ProgramAudienceGroups } from '../../../../../api/audienceRequirements';
import { allRoleIds, toServerGroups } from './audienceGroups';
import type { FlowDetails } from './DetailsStep';
import type { FormatKey } from './trainingRules';

export type FormStep = 'details' | 'goals' | 'format' | 'review';
export const FORM_STEPS: FormStep[] = ['details', 'goals', 'format', 'review'];
/** The whole flow, as the stepper shows it. Structure, Content and Members are the build. */
export const BUILD_STEPS = ['Details', 'Goals', 'Format', 'Review', 'Structure', 'Lessons', 'People'];
/** Where the build begins in BUILD_STEPS (and in the wizard's own steps, which line up from here). */
export const STRUCTURE_STEP = 4;

/** A capability recommendation a Training is built from: its role and capability. */
export interface TrainingRecommendation {
  roleId: string;
  capabilityId: string;
}

export interface TrainingSnapshot {
  v: 1;
  /** The step to reopen on; "build" once Structure has been asked for. */
  step: FormStep | 'build';
  /** Details, with the date as text. */
  details: Omit<FlowDetails, 'publishBy'> & { publishBy: string | null };
  /** The names of the roles in details.roleIds, for wherever the program's audience is shown. */
  roleNames?: string[];
  purpose: string;
  outcomes: string[];
  /** null = every capability the roles share, until the admin unticks one. */
  capabilityIds: string[] | null;
  format: FormatKey | null;
  preview: string | null;
  /**
   * A Guided Course: built exactly as a Course is, and held by the program too
   * (guided), which the backend publishes as a Guided Course (its card's
   * Continue then shows the editor and a Matches tab).
   */
  guided?: boolean;
  /**
   * What the program itself was last saved with from the flow. Details can be
   * edited on Review after that (and the snapshot is kept on closing), so a
   * program reopened is sent whatever differs from this, not from Details.
   * Absent from snapshots saved before it was kept: everything is sent then.
   */
  synced?: ProgramFields;
  /** Built from a capability recommendation: marked published once the build starts, whichever visit that is. */
  recommendation?: TrainingRecommendation;
}

export const snapshotOf = (state: {
  step: TrainingSnapshot['step'];
  details: FlowDetails;
  purpose: string;
  outcomes: string[];
  capabilityIds: string[] | null;
  format: FormatKey | null;
  preview: string | null;
  guided?: boolean;
  roleNames?: string[];
  synced?: ProgramFields | null;
  recommendation?: TrainingRecommendation | null;
}): TrainingSnapshot => {
  // DUMMY: the Audience Requirements groups stay in this browser (audienceGroups.ts saveGroups). The
  // snapshot is saved on the server, and a build that isn't the pilot must never find them there.
  const { groups, ...details } = state.details;
  return {
    v: 1,
    step: state.step,
    details: { ...details, publishBy: details.publishBy ? details.publishBy.toISOString() : null },
    ...(state.roleNames && state.roleNames.length > 0 ? { roleNames: state.roleNames } : {}),
    purpose: state.purpose,
    outcomes: state.outcomes,
    capabilityIds: state.capabilityIds,
    format: state.format,
    preview: state.preview,
    ...(state.guided ? { guided: true } : {}),
    ...(state.synced ? { synced: state.synced } : {}),
    ...(state.recommendation ? { recommendation: state.recommendation } : {})
  };
};

/** The Details a snapshot holds, with its date back as a date. */
export const detailsOf = (snapshot: TrainingSnapshot): FlowDetails => {
  const date = snapshot.details.publishBy ? new Date(snapshot.details.publishBy) : null;
  return { ...snapshot.details, publishBy: date && !Number.isNaN(date.getTime()) ? date : null };
};

/** The snapshot a program's saved build state carries, or null when it was not built in this flow. */
export const snapshotIn = (buildState: unknown): TrainingSnapshot | null => {
  const found = (buildState as { requirements?: { trainingFlow?: TrainingSnapshot } } | null)?.requirements
    ?.trainingFlow;
  return found && found.v === 1 && typeof found.details === 'object' && found.details !== null ? found : null;
};

/**
 * The build state saved with the program while the admin is still in the first
 * four steps: the snapshot, and beside it the name, purpose and sources where
 * the program's other screens look for them. Nothing is generated yet.
 */
export const formBuildState = (
  snapshot: TrainingSnapshot
): AIMDv2BuildState & { requirements: RequirementsData & { trainingFlow: TrainingSnapshot } } => ({
  currentStep: 0,
  highestCompletedStep: -1,
  requirements: {
    programName: snapshot.details.name.trim(),
    programDescription: snapshot.purpose.trim(),
    audience: [],
    audienceRules: [],
    tone: [],
    trainingFlow: snapshot
  },
  outcomes: { learningObjectives: [] },
  documents: { selectedDocIds: snapshot.details.docIds },
  preview: null,
  ai: { structure: null, content: null },
  generationId: null
});

// ---------------------------------------------------------------------------
// The program itself: what its card on the dashboard is made from
// ---------------------------------------------------------------------------

/** What the program stores of the flow: Details, and what it is built from. */
export interface ProgramFields {
  name: string;
  programType: ProgramTypeKey;
  learningPillarId: string | null;
  audienceRules: AudienceRule[];
  enrollmentMode: EnrollmentMode;
  /** Publish By, in unix seconds. */
  launchDate: number | null;
  ownerId: string | null;
  /** The roles the program is for (every type): whoever holds one is in it, narrowed by `audienceRules`. */
  roleIds: string[];
  /** The capabilities it builds, from its type's source. Empty: built on learning outcomes alone. */
  capabilityIds: string[];
  format: FormatKey | null;
  /** A Guided Course: built as a Course is, and published as a Guided Course. */
  guided: boolean;
  /**
   * Audience Requirements, with its backend on (audienceRequirementsBackend()):
   * the Read & Assess and Read Only groups, which are then the program's
   * audience. The backend derives roleIds, the narrowing and the enrolment mode
   * from them, so none of those is sent beside them. Absent otherwise.
   */
  audienceGroups?: ProgramAudienceGroups;
}

/** What the program is built from, beyond Details: chosen on Goals and Format, and in Add New Program. */
export interface BuildChoices {
  capabilityIds: string[];
  format: FormatKey | null;
  guided: boolean;
}

/**
 * The program's fields for these Details and build choices. Every program is
 * for its roles: the backend enrols whoever holds one (enrollmentMode 'roles'),
 * narrowed by the attributes chosen beside them, if any (all must match). It
 * builds the capabilities chosen on Goals, from its type's source (Role
 * Excellence the roles' own, Development and Standards the shared ones).
 */
export const programFieldsOf = (
  details: FlowDetails,
  catalog: MemberAttributeGroupCatalog[],
  build: BuildChoices,
  /** The groups are kept by the backend (audienceRequirementsBackend()): they are the audience. */
  groupsOnBackend = false
): ProgramFields => {
  const fields: ProgramFields = {
    name: details.name.trim(),
    programType: details.category ?? 'development',
    learningPillarId: details.stageId,
    audienceRules: applyAudienceMatchTypes(audienceRulesFromFlat(details.audienceIds, catalog), details.audienceMatch),
    enrollmentMode: 'roles',
    launchDate: details.publishBy ? Math.floor(details.publishBy.getTime() / 1000) : null,
    ownerId: details.ownerId,
    roleIds: details.roleIds,
    capabilityIds: build.capabilityIds,
    format: build.format,
    guided: build.guided
  };
  if (!groupsOnBackend || !details.groups) return fields;
  // The groups are the audience: the roles are every one they name (as the backend derives them), with no
  // narrowing of their own beside them. Neither is sent (programPatchOf); they are kept here to compare.
  return {
    ...fields,
    audienceRules: [],
    roleIds: allRoleIds(details.groups),
    audienceGroups: toServerGroups(details.groups)
  };
};

/**
 * The fields that differ between what the program holds and what Details now
 * says. Every field when what it holds is not known.
 */
export const changedFields = (held: ProgramFields | null, now: ProgramFields): (keyof ProgramFields)[] =>
  (Object.keys(now) as (keyof ProgramFields)[]).filter(
    (key) => !held || JSON.stringify(held[key]) !== JSON.stringify(now[key])
  );

/** A change to the program, as PATCH /programs/{id} takes it. */
export interface ProgramPatch {
  name?: string;
  learningPillarId?: string | null;
  programType?: ProgramTypeKey;
  ownerId?: string | null;
  roleIds?: string[];
  capabilityIds?: string[];
  format?: FormatKey | null;
  guided?: boolean;
  audienceRules?: AudienceRule[];
  enrollmentMode?: EnrollmentMode;
  /** false: the audience is saved, nobody enrolled. */
  enroll?: boolean;
  /** Audience Requirements: the groups, whole (the backend derives the roles and the narrowing from them). */
  audienceGroups?: ProgramAudienceGroups;
}

/**
 * What the program is sent for what changed since `held` (null when that is
 * not known: everything). `body` goes to PATCH /programs/{id}, with the
 * capabilities always beside the roles they come from, and the audience saved
 * without enrolling anyone (that waits for Invite & Finish). Publish By has an
 * endpoint of its own: `launchDate` says whether it changed.
 */
export const programPatchOf = (
  held: ProgramFields | null,
  now: ProgramFields
): { body: ProgramPatch; launchDate: boolean } => {
  const changed = changedFields(held, now);
  const has = (key: keyof ProgramFields) => changed.includes(key);
  if (now.audienceGroups) {
    // With groups the audience is the groups, sent whole, and never its roles or a narrowing: the backend
    // derives those (and refuses roles sent the old way to a program with several groups). The capabilities
    // still go beside the groups their Read & Assess roles come from.
    return {
      body: {
        ...(has('name') ? { name: now.name } : {}),
        ...(has('learningPillarId') ? { learningPillarId: now.learningPillarId } : {}),
        ...(has('programType') ? { programType: now.programType } : {}),
        ...(has('ownerId') ? { ownerId: now.ownerId } : {}),
        ...(has('audienceGroups') ? { audienceGroups: now.audienceGroups, enroll: false } : {}),
        ...(has('audienceGroups') || has('capabilityIds') ? { capabilityIds: now.capabilityIds } : {}),
        ...(has('format') ? { format: now.format } : {}),
        ...(has('guided') ? { guided: now.guided } : {})
      },
      launchDate: has('launchDate')
    };
  }
  return {
    body: {
      ...(has('name') ? { name: now.name } : {}),
      ...(has('learningPillarId') ? { learningPillarId: now.learningPillarId } : {}),
      ...(has('programType') ? { programType: now.programType } : {}),
      ...(has('ownerId') ? { ownerId: now.ownerId } : {}),
      ...(has('roleIds') || has('capabilityIds') ? { roleIds: now.roleIds, capabilityIds: now.capabilityIds } : {}),
      ...(has('format') ? { format: now.format } : {}),
      ...(has('guided') ? { guided: now.guided } : {}),
      ...(has('audienceRules') || has('enrollmentMode')
        ? { audienceRules: now.audienceRules, enrollmentMode: now.enrollmentMode, enroll: false }
        : {})
    },
    launchDate: has('launchDate')
  };
};

/** A name already in use by another program (whatever its capitals or spacing at the ends). */
export const nameTaken = (name: string, existing: string[], own: string | null): boolean => {
  const wanted = name.trim().toLowerCase();
  if (!wanted || wanted === (own ?? '').trim().toLowerCase()) return false;
  return existing.some((n) => n.trim().toLowerCase() === wanted);
};
